import { api } from "@/lib/api";
import {
  HotelDto,
  hotelDtoListSchema,
  HotelDtoSchema,
  hotelFromDto,
} from "@/models";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useToast } from "../use-toast";

export const getHotelsQueryOptions = queryOptions({
  queryKey: ["hotels"],
  queryFn: async () => {
    const result: Array<HotelDto> = [];
    let page = 0;
    let hasNextPage = true;
    while (hasNextPage) {
      page += 1;
      const res = await api.getWithCache("/api/hotels", hotelDtoListSchema, {
        query: {
          page: page.toString(),
        },
      });
      result.push(...res.Data);
      hasNextPage = res.PageInfo.HasNextPage;
    }
    return result.map(hotelFromDto);
  },
});

export function useGetHotels<TERROR extends Error>(opts?: {
  onError?: (error: TERROR) => void;
  enabled?: boolean;
}) {
  const { toast } = useToast();

  const q = useQuery({
    ...getHotelsQueryOptions,
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getHotelsError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}

export function getHotelQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["hotels", id],
    queryFn: () =>
      api
        .get("/api/hotels/$id", HotelDtoSchema, {
          pathReplace: { $id: id },
        })
        .then(hotelFromDto),
  });
}

export function useGetHotel<TERROR extends Error>(
  id: string,
  opts?: { onError?: (error: TERROR) => void; enabled?: boolean },
) {
  const { toast } = useToast();
  const q = useQuery({
    ...getHotelQueryOptions(id),
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getHotelDetailError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}
